import React from "react";
import { Grid2 } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Control, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useQueryClient } from "@tanstack/react-query";
import { subDays } from "date-fns";
import { useSnackbar } from "notistack";
import { Investment } from "@rentablo/api/apiv1";
import { sameDateInUTC } from "@features/cashbackWizard2.0/utils/helpers";
import { RHFDatePicker, RHFTextField } from "@rentablo/components";
import { CACHE_KEYS, useAddOrUpdateHistoricQuotes } from "@rentablo/api/apiv1";
import { LoadingButton } from "@rentablo/components";

interface FormFields {
  quote: string;
  quoteFrom: string;
}

interface Props {
  investment?: Investment;
  currency?: string;
  onClose?: () => void;
}
export const InvestmentTableAddQuoteModal = ({
  investment,
  currency,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const schema = Yup.object().shape({
    quote: Yup.number().required(),
    quoteFrom: Yup.string().required(),
  });

  const { handleSubmit, control, getValues } = useForm<FormFields>({
    resolver: yupResolver(schema),
    defaultValues: {
      quote: "0",
      quoteFrom: new Date().toString(),
    },
  });

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { mutate: saveQuote, isPending } = useAddOrUpdateHistoricQuotes({
    onSuccess: async () => {
      onClose && onClose();
      enqueueSnackbar(
        `${t("domains.investments.investmentTable.addQuote.successMessage")}`,
        {
          autoHideDuration: 3000,
          variant: "success",
        }
      );
      await queryClient.invalidateQueries({
        queryKey: [CACHE_KEYS.LIST_AND_SEARCH_ALL_INVESTMENTS],
      });
    },
  });
  const onSubmit = () => {
    const { quote, quoteFrom } = getValues();
    saveQuote({
      addOrUpdateHistoricQuoteParams: {
        investmentId: investment?.id,
        dateValuePairs: [
          {
            date: sameDateInUTC(new Date(quoteFrom)).toISOString(),
            value: Number.parseFloat(quote),
          },
        ],
      },
    });
  };

  const quote = useWatch({ control, name: "quote" });
  const quoteFrom = useWatch({ control, name: "quoteFrom" });
  const isSaveBtnDisabled = !quote || Number(quote) <= 0 || !quoteFrom;

  return (
    <Grid2 container spacing={4}>
      <Grid2
        size={{
          xs: 12,
          lg: 6,
          md: 6,
        }}
      >
        <RHFTextField
          name="quote"
          label={t("domains.investments.investmentTable.addQuote.quote", {
            context: currency ? "withCurrency" : undefined,
            currency,
          })}
          control={control}
          fullWidth
          type="number"
        />
      </Grid2>
      <Grid2
        size={{
          xs: 12,
          lg: 6,
          md: 6,
        }}
      >
        <RHFDatePicker
          name="quoteFrom"
          label={t("domains.investments.investmentTable.addQuote.quoteFrom")}
          control={control as Control<any>}
          maxDate={subDays(new Date(), 1)}
        />
      </Grid2>
      <Grid2
        size={{
          lg: 12,
        }}
      >
        <LoadingButton
          variant="primary"
          type="submit"
          size="small"
          isFetching={isPending}
          sx={{ marginTop: 5 }}
          onClick={handleSubmit(onSubmit)}
          disabled={isSaveBtnDisabled}
        >
          {t("domains.investments.investmentTable.addQuote.save")}
        </LoadingButton>
      </Grid2>
    </Grid2>
  );
};
